import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import api from '../services/api';
import { MegaphoneIcon, MagnifyingGlassIcon, CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';

/**
 * Массовая рассылка в Telegram-чаты клубов.
 * Доступ: admin/superadmin и операторы 2-й линии (backend CanBroadcast).
 * Отправка идёт фоновой задачей; история обновляется, пока рассылка идёт.
 */

interface Recipient { chatId: string; title: string; city: string; billingStatus: string }
interface BroadcastRow {
  id: string; text: string; status: 'queued' | 'sending' | 'done' | 'failed';
  total: number; sent: number; failed: number;
  createdAt: string; finishedAt?: string | null; createdBy?: string | null;
  results?: { chatId: string; title: string; ok: boolean; error?: string }[];
}

const MAX_LEN = 4000;
const STATUS_RU: Record<string, string> = { queued: 'в очереди', sending: 'отправляется', done: 'отправлено', failed: 'ошибка' };
const STATUS_CLS: Record<string, string> = {
  queued: 'bg-muted text-muted-foreground',
  sending: 'bg-amber-500/15 text-amber-700 dark:text-amber-400',
  done: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400',
  failed: 'bg-red-500/15 text-red-700 dark:text-red-400',
};
const BILLING_RU: Record<string, string> = { active: 'платит', new: 'новый', paused: 'пауза', lost: 'ушёл' };
const plural = (n: number, one: string, few: string, many: string) => {
  const m10 = n % 10, m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return one;
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return few;
  return many;
};
const fmt = (iso?: string | null) => (iso ? new Date(iso).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '');

const BroadcastPage: React.FC = () => {
  const [recipients, setRecipients] = useState<Recipient[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');
  const [text, setText] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState<BroadcastRow[]>([]);
  const [openId, setOpenId] = useState<string | null>(null);
  const [details, setDetails] = useState<Record<string, BroadcastRow>>({});

  const loadHistory = useCallback(async () => {
    try {
      const res = await api.get('/api/clients/broadcasts');
      setHistory(res.data?.data || []);
    } catch { /* toast once on mount below */ }
  }, []);

  useEffect(() => {
    api.get('/api/clients/broadcasts/recipients')
      .then((res) => setRecipients(res.data?.data || []))
      .catch((e) => toast.error(e?.response?.data?.message || 'Не удалось загрузить список клубов'));
    loadHistory();
  }, [loadHistory]);

  // Пока какая-то рассылка идёт — обновляем историю и открытую карточку.
  const active = history.some((h) => h.status === 'queued' || h.status === 'sending');
  useEffect(() => {
    if (!active) return;
    const t = setInterval(() => {
      loadHistory();
      if (openId) api.get(`/api/clients/broadcasts/${openId}`).then((r) => setDetails((d) => ({ ...d, [openId]: r.data?.data }))).catch(() => {});
    }, 2000);
    return () => clearInterval(t);
  }, [active, openId, loadHistory]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? recipients.filter((r) => `${r.title} ${r.city}`.toLowerCase().includes(q)) : recipients;
  }, [recipients, search]);

  const toggle = (id: string) => setSelected((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const selectAll = () => setSelected(new Set(recipients.map((r) => r.chatId)));
  const selectActive = () => setSelected(new Set(recipients.filter((r) => r.billingStatus === 'active').map((r) => r.chatId)));
  const selectFiltered = () => setSelected((s) => { const n = new Set(s); filtered.forEach((r) => n.add(r.chatId)); return n; });

  const canSend = text.trim().length > 0 && text.length <= MAX_LEN && selected.size > 0 && !sending;

  const send = async () => {
    setSending(true);
    try {
      const res = await api.post('/api/clients/broadcasts', { text: text.trim(), chatIds: Array.from(selected) });
      toast.success(res.data?.message || 'Рассылка запущена');
      setText(''); setSelected(new Set()); setConfirming(false);
      const id = res.data?.data?.id;
      if (id) { setOpenId(id); setDetails((d) => ({ ...d, [id]: res.data.data })); }
      loadHistory();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Не удалось запустить рассылку');
    } finally {
      setSending(false);
    }
  };

  const openRow = async (id: string) => {
    if (openId === id) { setOpenId(null); return; }
    setOpenId(id);
    try {
      const r = await api.get(`/api/clients/broadcasts/${id}`);
      setDetails((d) => ({ ...d, [id]: r.data?.data }));
    } catch { /* ignore */ }
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <MegaphoneIcon className="h-7 w-7 text-primary" />
        <div>
          <h1 className="text-2xl font-bold text-foreground">Рассылка клубам</h1>
          <p className="text-sm text-muted-foreground">Одно сообщение от бота в выбранные Telegram-чаты клубов: сбои, техработы, обновления.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_360px] gap-4">
        {/* Текст */}
        <section className="bg-card border border-border rounded-lg p-4 flex flex-col gap-3">
          <label htmlFor="bc-text" className="text-sm font-medium text-foreground">Текст сообщения</label>
          <textarea
            id="bc-text"
            value={text}
            onChange={(e) => { setText(e.target.value); setConfirming(false); }}
            rows={9}
            placeholder={'Например:\n⚠️ Сейчас сбой на стороне FACEIT, античит не пускает. Мы следим, напишем, когда восстановится.'}
            className="w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
          <div className={`text-xs text-right ${text.length > MAX_LEN ? 'text-red-600' : 'text-muted-foreground'}`}>{text.length} / {MAX_LEN}</div>

          {text.trim() && (
            <div>
              <div className="text-xs text-muted-foreground mb-1">Так увидят клубы:</div>
              <div className="max-w-md rounded-2xl rounded-bl-md bg-muted px-3 py-2 text-sm whitespace-pre-wrap break-words">{text.trim()}</div>
            </div>
          )}

          <div className="mt-auto pt-2 flex flex-wrap items-center gap-2 border-t border-border">
            {!confirming ? (
              <button
                type="button"
                disabled={!canSend}
                onClick={() => setConfirming(true)}
                className="h-10 px-4 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Отправить в {selected.size} {plural(selected.size, 'чат', 'чата', 'чатов')}
              </button>
            ) : (
              <>
                <span className="text-sm text-foreground">Отправить сообщение в {selected.size} {plural(selected.size, 'чат', 'чата', 'чатов')}? Отменить после отправки нельзя.</span>
                <button type="button" onClick={send} disabled={sending} className="h-10 px-4 rounded-md bg-red-600 text-white hover:bg-red-700 disabled:opacity-60">
                  {sending ? 'Запускаем…' : 'Да, отправить'}
                </button>
                <button type="button" onClick={() => setConfirming(false)} className="h-10 px-4 rounded-md border border-border hover:bg-accent">Отмена</button>
              </>
            )}
          </div>
        </section>

        {/* Получатели */}
        <section className="bg-card border border-border rounded-lg flex flex-col max-h-[560px]">
          <div className="p-3 border-b border-border space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="font-medium text-foreground">Клубы</span>
              <span className="text-muted-foreground">выбрано {selected.size} из {recipients.length}</span>
            </div>
            <div className="relative">
              <MagnifyingGlassIcon className="h-4 w-4 absolute left-2.5 top-2.5 text-muted-foreground" />
              <input
                id="bc-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Поиск по названию или городу"
                className="w-full h-9 pl-8 pr-2 rounded-md border border-input bg-background text-sm"
              />
            </div>
            <div className="flex flex-wrap gap-1.5 text-xs">
              <button type="button" onClick={selectAll} className="px-2 py-1 rounded border border-border hover:bg-accent">Все</button>
              <button type="button" onClick={selectActive} className="px-2 py-1 rounded border border-border hover:bg-accent">Только платящие</button>
              {search && <button type="button" onClick={selectFiltered} className="px-2 py-1 rounded border border-border hover:bg-accent">Найденные</button>}
              <button type="button" onClick={() => setSelected(new Set())} className="px-2 py-1 rounded border border-border hover:bg-accent">Снять</button>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="p-4 text-xs text-muted-foreground text-center">Ничего не найдено</div>
            ) : filtered.map((r) => (
              <label key={r.chatId} className="flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-accent/40 cursor-pointer border-b border-border/50">
                <input type="checkbox" checked={selected.has(r.chatId)} onChange={() => toggle(r.chatId)} className="h-4 w-4 accent-[hsl(var(--primary))]" />
                <span className="flex-1 min-w-0 truncate text-foreground">{r.title}</span>
                {r.city && <span className="text-xs text-muted-foreground shrink-0">{r.city}</span>}
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground shrink-0">{BILLING_RU[r.billingStatus] || r.billingStatus}</span>
              </label>
            ))}
          </div>
        </section>
      </div>

      {/* История */}
      <section className="bg-card border border-border rounded-lg">
        <div className="px-4 py-3 border-b border-border text-sm font-medium text-foreground">История рассылок</div>
        {history.length === 0 ? (
          <div className="p-6 text-sm text-muted-foreground text-center">Рассылок ещё не было</div>
        ) : history.map((h) => {
          const d = details[h.id];
          return (
            <div key={h.id} className="border-b border-border last:border-b-0">
              <button type="button" onClick={() => openRow(h.id)} className="w-full text-left px-4 py-3 flex flex-wrap items-center gap-x-4 gap-y-1 hover:bg-accent/30">
                <span className="text-xs text-muted-foreground w-24 shrink-0">{fmt(h.createdAt)}</span>
                <span className="flex-1 min-w-[200px] text-sm text-foreground truncate">{h.text}</span>
                <span className="text-xs text-muted-foreground">{h.createdBy}</span>
                <span className="text-xs tabular-nums text-foreground">{h.sent}/{h.total}{h.failed ? <span className="text-red-600"> · ошибок {h.failed}</span> : null}</span>
                <span className={`text-[11px] px-2 py-0.5 rounded-full ${STATUS_CLS[h.status]}`}>{STATUS_RU[h.status]}</span>
              </button>
              {openId === h.id && (
                <div className="px-4 pb-4 grid gap-3 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
                  <div className="rounded-md bg-muted/50 p-3 text-sm whitespace-pre-wrap break-words">{h.text}</div>
                  <div className="max-h-64 overflow-y-auto text-xs space-y-1">
                    {(d?.results || []).length === 0 && <div className="text-muted-foreground">{h.status === 'done' || h.status === 'failed' ? 'Нет данных' : 'Отправляется…'}</div>}
                    {(d?.results || []).map((r) => (
                      <div key={r.chatId} className="flex items-start gap-2">
                        {r.ok ? <CheckCircleIcon className="h-4 w-4 text-emerald-600 shrink-0" /> : <XCircleIcon className="h-4 w-4 text-red-600 shrink-0" />}
                        <span className="text-foreground">{r.title}</span>
                        {!r.ok && r.error && <span className="text-red-600 break-words">— {r.error}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </section>
    </div>
  );
};

export default BroadcastPage;
